import axios from 'axios';

// Backend API URL configuration
const BACKEND_URL = process.env.REACT_APP_API_URL || 'http://localhost:3002/api';

// Log the backend URL for debugging (remove in production)
console.log('🔗 Backend API URL:', BACKEND_URL);

// Create axios instance with base configuration
const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle token expiration
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('token');
      localStorage.removeItem('commercialData');
      localStorage.removeItem('userRole');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Authentication API
export const authAPI = {
  login: async (username, password) => {
    const response = await api.post('/auth/login', { username, password });
    return response.data;
  },
  
  logout: async () => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('commercialData');
      localStorage.removeItem('userRole');
    }
  },
};

// Commercials API (for Technico)
export const commercialsAPI = {
  // Get all commercials for assignment
  getAll: async () => {
    const response = await api.get('/commercials');
    return response.data;
  },
};

// Documents API
export const documentsAPI = {
  // Get factures from last 4 months
  getFactures: async () => {
    const response = await api.get('/documents/factures');
    return response.data;
  },

  // Get devis from last 4 months
  getDevis: async () => {
    const response = await api.get('/documents/devis');
    return response.data;
  },

  // Get proforma from last 4 months
  getProforma: async () => {
    const response = await api.get('/documents/proforma');
    return response.data;
  },

  // Refresh documents (force fetch from database)
  refreshDocuments: async () => {
    const response = await api.post('/documents/refresh');
    return response.data;
  },

  // Search factures by Num_facture (not limited by date)
  searchFactures: async (number) => {
    const response = await api.get(`/documents/search/factures?number=${encodeURIComponent(number)}`);
    return response.data;
  },

  // Search devis by Num_devis (not limited by date)
  searchDevis: async (number) => {
    const response = await api.get(`/documents/search/devis?number=${encodeURIComponent(number)}`);
    return response.data;
  },

  // Search proforma by Num_facture (not limited by date)
  searchProforma: async (number) => {
    const response = await api.get(`/documents/search/proforma?number=${encodeURIComponent(number)}`);
    return response.data;
  },
};

// Declaration Details API
export const declarationDetailsAPI = {
  // Get declaration details by declaration ID
  getByDeclarationId: async (declarationId) => {
    const response = await api.get(`/declaration-details/${declarationId}`);
    return response.data;
  },

  // Create declaration detail
  create: async (declarationId, uuidRelated, type) => {
    const response = await api.post('/declaration-details', {
      declaration_id: declarationId,
      uuid_related: uuidRelated,
      type: type
    });
    return response.data;
  },

  // Delete declaration detail
  delete: async (id) => {
    const response = await api.delete(`/declaration-details/${id}`);
    return response.data;
  },
};

// Declarations API
export const declarationsAPI = {
  // Get all declarations for a commercial
  getAll: async () => {
    const response = await api.get('/declarations');
    return response.data;
  },

  // Get declarations by type
  getByType: async (type) => {
    const response = await api.get(`/declarations?type=${type}`);
    return response.data;
  },

  // Create new declaration
  create: async (declarationData) => {
    const formData = new FormData();
    
    // Add all fields to FormData
    Object.keys(declarationData).forEach(key => {
      if (declarationData[key] !== null && declarationData[key] !== undefined) {
        formData.append(key, declarationData[key]);
      }
    });

    const response = await api.post('/declarations', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Update declaration (for Technico and Commercial for unvalidated declarations)
  update: async (id, declarationData) => {
    const formData = new FormData();
    
    // Add all fields to FormData, handling undefined values
    Object.keys(declarationData).forEach(key => {
      if (declarationData[key] !== null && declarationData[key] !== undefined && declarationData[key] !== '') {
        formData.append(key, declarationData[key]);
      }
    });

    const response = await api.put(`/declarations/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Delete declaration (for Technico)
  delete: async (id) => {
    const response = await api.delete(`/declarations/${id}`);
    return response.data;
  },

  // Validate declaration (for Commercial)
  validate: async (id) => {
    const response = await api.post(`/declarations/${id}/validate`);
    return response.data;
  },

  // Upload file
  uploadFile: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await api.post('/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
};

export default api; 